/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type FetchStatus = "idle" | "fetching" | "completed" | "error";
export type FetchFunction<Parameters extends unknown[]> = (...args: Parameters) => void;
interface DataState<T> {
  status: FetchStatus;
  data?: T;
  error?: Error;
}

export const useDataFetching = <Parameters extends unknown[], Result>(fetchFn: (...args: Parameters) => Promise<Result>): [FetchFunction<Parameters>, Result | undefined, FetchStatus, Error | undefined] => {
  const [dataState, setDataState] = useState<DataState<Result>>({ status: "idle" });
  const isMounted = useRef<boolean>(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  });

  const fetch = useCallback((...args: Parameters) => {
    setDataState({ status: "fetching", data: undefined, error: undefined });
    fetchFn(...args)
      .then((response) => {
        if (isMounted.current) {
          setDataState({ status: "completed", data: response });
        }
      })
      .catch((err) => {
        if (isMounted.current) {
          setDataState({ status: "error", data: undefined, error: err });
        }
      });
  }, [fetchFn]);

  return useMemo(() => ([fetch, dataState.data, dataState.status, dataState.error]), [dataState.data, dataState.error, dataState.status, fetch]);
};
